import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swords, Trophy, Users, Shield, History, Layers, Sparkles, ChevronRight, Star, Crown, ShieldOff } from 'lucide-react';
import api from '../../lib/api';
import { RARITY_UI } from '../../lib/rarity';
import RarityBadge from '../../components/RarityBadge';
import MatchmakingModal from '../../components/arena3d/MatchmakingModal';
import RoundLengthToggle from '../../components/RoundLengthToggle';
import { Skeleton, SkeletonList } from '../../components/Skeleton';

const RANK_COLORS = {
  Bronze:      '#B08B58',
  Silver:      '#C0C0C0',
  Gold:        '#E9C46A',
  Platinum:    '#8ECAE6',
  Diamond:     '#FF6EC7',
  Master:      '#A78BFA',
  Grandmaster: '#F43F5E',
};

export default function BattleHub() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [cards, setCards] = useState([]);
  const [recent, setRecent] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [arenaRating, setArenaRating] = useState(null);
  const [seasonMe, setSeasonMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mmMode, setMmMode] = useState(null); // 'ranked' | 'casual' | null
  const [claimingSeason, setClaimingSeason] = useState(null);

  useEffect(() => {
    // Deck loading removed in iter74 — tap combat only uses single cards now.
    Promise.all([
      api.get('/battle-stats/me').then(r => r.data).catch(() => null),
      api.get('/cards?limit=200').then(r => r.data).catch(() => []),
      api.get('/battles/mine?limit=5').then(r => r.data).catch(() => []),
      api.get('/battle-leaderboard?limit=10').then(r => r.data).catch(() => ({ leaderboard: [] })),
      api.get('/arena/rating/me').then(r => r.data?.rating).catch(() => null),
      api.get('/arena/seasons/me').then(r => r.data).catch(() => null),
    ]).then(([s, c, h, lb, ar, sm]) => {
      setStats(s); setCards(c || []); setRecent(h || []);
      setLeaderboard(lb.leaderboard || []);
      setArenaRating(ar);
      setSeasonMe(sm);
      setLoading(false);
    });
  }, []);

  const claimReward = async (seasonId) => {
    setClaimingSeason(seasonId);
    try {
      await api.post(`/arena/seasons/claim/${seasonId}`);
      const sm = await api.get('/arena/seasons/me').then(r => r.data).catch(() => null);
      setSeasonMe(sm);
    } catch (e) {
      // Silent; UI will still show the button on failure
    } finally {
      setClaimingSeason(null);
    }
  };

  if (loading) return (
    <div className="space-y-5 pb-4" data-testid="battle-hub-loading">
      <div className="space-y-2">
        <Skeleton className="h-2.5 w-16 rounded" />
        <Skeleton className="h-8 w-48 rounded" />
        <Skeleton className="h-3 w-64 rounded opacity-70" />
      </div>
      <Skeleton className="h-16 rounded-2xl" />
      <div className="grid grid-cols-2 gap-2">
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
      </div>
      <SkeletonList rows={3} avatar />
    </div>
  );

  const hasCards = cards.length > 0;
  const rankColor = RANK_COLORS[stats?.rank?.tier] || '#E9C46A';

  return (
    <div className="space-y-5 pb-4" data-testid="battle-hub-root">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Battle</p>
        <h2 className="font-serif text-3xl">Arena Lobby</h2>
        <p className="text-xs text-muted-foreground mt-1">Command your Battle Cards. Climb the ranks. Compete for glory.</p>
      </div>

      {arenaRating && (arenaRating.wins + arenaRating.losses + arenaRating.draws) > 0 && (
        <div
          className="rounded-2xl border border-white/10 bg-black/40 p-3 flex items-center gap-3"
          data-testid="arena-v3-rating-badge"
        >
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-[10px] font-bold uppercase tracking-widest"
               style={{ background: `${RANK_COLORS[arenaRating.tier] || '#94A3B8'}30`,
                        color: RANK_COLORS[arenaRating.tier] || '#E9C46A',
                        border: `1px solid ${RANK_COLORS[arenaRating.tier] || '#94A3B8'}` }}>
            {String(arenaRating.tier || '?').slice(0, 3)}
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Three-Lane Arena rating</p>
            <p className="text-sm">
              <span className="text-white font-mono" data-testid="arena-v3-rating-value">{arenaRating.rating}</span>
              <span className="text-white/50 mx-1.5">·</span>
              <span className="text-emerald-300">{arenaRating.wins}W</span>
              <span className="text-white/40 mx-0.5">/</span>
              <span className="text-rose-300">{arenaRating.losses}L</span>
              {arenaRating.draws > 0 && (
                <>
                  <span className="text-white/40 mx-0.5">/</span>
                  <span className="text-white/60">{arenaRating.draws}D</span>
                </>
              )}
              <span className="text-white/40 mx-1.5">·</span>
              <span className="text-white/70">{arenaRating.tier}</span>
            </p>
          </div>
        </div>
      )}

      {seasonMe?.season && (
        <SeasonBadge
          season={seasonMe.season}
          stats={seasonMe.stats}
          pendingRewards={seasonMe.pending_rewards || []}
          onClaim={claimReward}
          claimingSeason={claimingSeason}
          onOpenLeaderboard={() => nav('/battle/seasons')}
        />
      )}

      {!hasCards ? <NoCardsEmpty /> : (
        <>
          <RankHero stats={stats} rankColor={rankColor} onOpenProfile={() => nav('/battle/profile')} />

          {!hasCards && (
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-3xl bg-gradient-to-br from-amber-500/15 to-accent/5 border border-amber-400/30 p-4"
              data-testid="battle-hub-no-cards"
            >
              <div className="flex items-start gap-3">
                <Layers className="w-5 h-5 text-amber-300 mt-0.5" />
                <div className="flex-1">
                  <p className="font-serif text-base">You need a Battle Card to fight</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Every wildlife discovery you make mints a procedural Battle Card.</p>
                  <button onClick={() => nav('/discover')} data-testid="battle-hub-go-discover-btn"
                    className="mt-3 px-4 py-2 rounded-full bg-accent text-black text-xs font-semibold uppercase tracking-widest">
                    Go discover
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Modes — single-card tap combat is the active battle style. Decks
             (multi-card loadouts) are shelved alongside Arena V3 card-game. */}
          <RoundLengthToggle />

          <section>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Choose your mode</p>
            <div className="grid grid-cols-1 gap-2">
              <ModeCard testid="mode-practice" icon={Shield}
                label="Practice · vs AI"
                desc="Warm up against an AI explorer. No stakes, no rating changes."
                available={hasCards}
                onClick={() => nav('/battle/practice')} />
              <ModeCard testid="mode-ranked" icon={Trophy}
                label="Ranked PvP"
                desc={hasCards ? "Fight real explorers. Wins/losses update your rating." : "Capture a Battle Card first."}
                available={hasCards}
                onClick={() => hasCards && setMmMode('ranked')} />
              <ModeCard testid="mode-casual" icon={Users}
                label="Casual PvP"
                desc={hasCards ? "Face live opponents with no rating stakes." : "Capture a Battle Card first."}
                available={hasCards}
                onClick={() => hasCards && setMmMode('casual')} />
              <ModeCard testid="mode-friend" icon={Sparkles}
                label="Friend Duel"
                desc="Challenge a specific explorer to a private battle."
                available={hasCards}
                onClick={() => hasCards && nav('/battle/duels')} />
            </div>
          </section>

          {/* Recent matches */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Recent matches</p>
              <button onClick={() => nav('/battle/history')} data-testid="btn-view-history" className="text-[10px] uppercase tracking-widest text-accent">View history</button>
            </div>
            {recent.length === 0 ? (
              <div className="rounded-2xl bg-card border border-white/5 p-6 text-center text-xs text-muted-foreground" data-testid="battle-hub-no-matches">
                No matches yet — hop into Practice to warm up.
              </div>
            ) : (
              <div className="rounded-2xl bg-card border border-white/5 overflow-hidden">
                {recent.map(b => <RecentRow key={b.battle_id} battle={b} />)}
              </div>
            )}
          </section>

          {/* Season Rewards */}
          <SeasonRewards rating={stats?.rating || 1000} rank={stats?.rank} />

          {/* Leaderboard preview */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Global battle leaderboard</p>
            </div>
            {leaderboard.length === 0 ? (
              <div className="rounded-2xl bg-card border border-white/5 p-4 text-center text-xs text-muted-foreground" data-testid="battle-hub-leaderboard-empty">
                Be the first to log a ranked victory.
              </div>
            ) : (
              <div className="rounded-2xl bg-card border border-white/5 overflow-hidden">
                {leaderboard.slice(0, 5).map((row, i) => (
                  <div key={row.user_id} className="flex items-center gap-2 p-2.5 border-b border-white/5 last:border-0" data-testid="battle-lb-row">
                    <span className="w-6 text-center font-mono text-xs text-muted-foreground">#{i + 1}</span>
                    <img src={row.picture || 'https://i.pravatar.cc/40'} alt="" className="w-7 h-7 rounded-full object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs truncate">{row.name}</p>
                      <p className="text-[10px] font-mono" style={{ color: RANK_COLORS[row.rank.tier] || '#9CA3AF' }}>{row.rank.label}</p>
                    </div>
                    <span className="font-mono text-xs text-accent tabular-nums">{row.rating}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      <MatchmakingModal
        open={!!mmMode}
        mode={mmMode}
        cards={cards}
        onClose={() => setMmMode(null)}
        onMatchFound={(battleId) => {
          setMmMode(null);
          nav(`/battle/live/${battleId}`);
        }}
      />
    </div>
  );
}

function NoCardsEmpty() {
  const nav = useNavigate();
  return (
    <div className="rounded-3xl bg-card border border-white/5 p-8 text-center space-y-3" data-testid="battle-no-cards-empty">
      <ShieldOff className="w-10 h-10 mx-auto text-muted-foreground" strokeWidth={1.5} />
      <p className="font-serif text-xl">You need Battle Cards to enter the Arena</p>
      <p className="text-xs text-muted-foreground max-w-sm mx-auto">Every wildlife discovery you make mints a procedural Battle Card. Get out there and photograph some species!</p>
      <button data-testid="btn-go-discover" onClick={() => nav('/discover')}
        className="mt-2 px-5 py-3 rounded-full bg-accent text-black text-xs font-semibold uppercase tracking-widest">
        Go Discover
      </button>
    </div>
  );
}

function RankHero({ stats, rankColor, onOpenProfile }) {
  const total = (stats?.total_battles || 0);
  const winRate = stats?.win_rate || 0;
  return (
    <motion.button
      type="button"
      whileHover={{ y: -2 }}
      onClick={onOpenProfile}
      data-testid="battle-rank-hero"
      className="w-full text-left rounded-3xl overflow-hidden bg-gradient-to-br from-primary/25 via-primary/5 to-transparent border border-white/10 relative"
    >
      <div
        className="absolute -right-10 -top-10 w-48 h-48 rounded-full blur-3xl opacity-25"
        style={{ background: rankColor }}
      />
      <div className="relative p-5 flex items-center gap-4">
        <div className="shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center border-2"
             style={{ borderColor: rankColor, background: `${rankColor}18` }}>
          <Crown className="w-8 h-8" style={{ color: rankColor }} strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Battle Rating</p>
          <p className="font-mono text-3xl tabular-nums leading-tight" style={{ color: rankColor }} data-testid="battle-rating">{stats?.rating ?? 1000}</p>
          <p className="text-sm mt-0.5" data-testid="battle-rank-label" style={{ color: rankColor }}>{stats?.rank?.label || 'Unranked'}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Record</p>
          <p className="font-mono text-sm mt-1"><span className="text-emerald-400">{stats?.wins ?? 0}W</span> · <span className="text-red-400">{stats?.losses ?? 0}L</span></p>
          <p className="text-[10px] text-muted-foreground">{total ? `${winRate}% win rate` : 'No matches yet'}</p>
        </div>
      </div>
    </motion.button>
  );
}

function ModeCard({ testid, icon: Icon, label, desc, available, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!available}
      data-testid={testid}
      className={`w-full text-left rounded-2xl p-4 border transition-colors flex items-center gap-3 ${
        available ? 'bg-card border-white/10 hover:border-accent/50 hover:bg-white/5'
                  : 'bg-card/50 border-white/5 opacity-60 cursor-not-allowed'
      }`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${available ? 'bg-accent/15 text-accent' : 'bg-white/5 text-muted-foreground'}`}>
        <Icon className="w-5 h-5" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-serif text-base leading-tight">{label}</p>
        <p className="text-[11px] text-muted-foreground truncate">{desc}</p>
      </div>
      {available && <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
    </button>
  );
}

function RecentRow({ battle }) {
  const won = battle.winner === 'challenger';
  const ui = RARITY_UI[battle.opponent_snapshot?.rarity] || RARITY_UI.common;
  return (
    <div className="flex items-center gap-2 p-3 border-b border-white/5 last:border-0" data-testid="battle-hub-recent-row">
      <div className={`w-1.5 h-8 rounded-full ${won ? 'bg-emerald-400' : 'bg-red-400'}`} />
      <div className="min-w-0 flex-1">
        <p className="text-xs truncate">
          {battle.challenger_snapshot?.name}
          <span className="text-muted-foreground mx-1.5">vs</span>
          <span style={{ color: ui.color }}>{battle.opponent_snapshot?.name}</span>
        </p>
        <p className="text-[10px] text-muted-foreground">
          {(battle.mode || 'practice').toUpperCase()} · {new Date(battle.created_at).toLocaleDateString()}
        </p>
      </div>
      <span className={`font-mono text-[10px] uppercase tracking-widest ${won ? 'text-emerald-400' : 'text-red-400'}`}>
        {won ? 'W' : 'L'} {battle.round_wins.challenger}–{battle.round_wins.opponent}
      </span>
    </div>
  );
}

function SeasonRewards({ rating, rank }) {
  // Next tier milestones
  const nextTier = rank ? rank.tier_max : 1000;
  const progress = Math.max(0, Math.min(100, ((rating - (rank?.tier_min || 0)) / Math.max(1, (rank?.tier_max || 1000) - (rank?.tier_min || 0))) * 100));
  return (
    <section data-testid="battle-hub-season">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Season progress</p>
      <div className="rounded-2xl bg-card border border-white/5 p-4">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Next tier</span>
          <span className="font-mono">{Math.max(0, nextTier - rating)} rating away</span>
        </div>
        <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }}
            transition={{ duration: 0.7 }}
            className="h-full bg-gradient-to-r from-emerald-400 to-accent" />
        </div>
        <div className="mt-3 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>Season 1 · beta</span>
          <span>Top ranks earn cosmetic emblems (soon)</span>
        </div>
      </div>
    </section>
  );
}

function SeasonBadge({ season, stats, pendingRewards = [], onClaim, claimingSeason, onOpenLeaderboard }) {
  const daysLeft = (() => {
    try {
      const end = new Date(season.ends_at);
      const now = new Date();
      const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return Math.max(0, diff);
    } catch { return null; }
  })();
  const tierColor = RANK_COLORS[stats?.tier] || '#E9C46A';
  const wins = stats?.wins || 0;
  const losses = stats?.losses || 0;
  const draws = stats?.draws || 0;
  const played = wins + losses + draws;
  return (
    <section data-testid="season-badge" className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 via-black/30 to-emerald-500/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300/80">Ranked Season</p>
          <p className="font-serif text-lg truncate" data-testid="season-name">{season.name}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {season.season_id}{daysLeft !== null ? ` · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left` : ''}
          </p>
        </div>
        <button
          onClick={onOpenLeaderboard}
          data-testid="season-leaderboard-btn"
          className="text-[10px] uppercase tracking-widest text-amber-300 hover:text-amber-200 flex items-center gap-1"
        >
          Leaderboard <ChevronRight className="w-3 h-3" />
        </button>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-black/30 border border-white/5 p-2">
          <p className="text-[9px] uppercase text-muted-foreground">Rank</p>
          <p className="font-mono text-sm" style={{ color: tierColor }} data-testid="season-rank">
            {stats?.tier || 'Unranked'}
          </p>
        </div>
        <div className="rounded-lg bg-black/30 border border-white/5 p-2">
          <p className="text-[9px] uppercase text-muted-foreground">Rating</p>
          <p className="font-mono text-sm text-white" data-testid="season-rating">
            {stats?.rating ?? '—'}
          </p>
        </div>
        <div className="rounded-lg bg-black/30 border border-white/5 p-2">
          <p className="text-[9px] uppercase text-muted-foreground">Peak</p>
          <p className="font-mono text-sm text-amber-300" data-testid="season-peak">
            {stats?.peak_rating ?? '—'}
          </p>
        </div>
      </div>
      {played > 0 && (
        <p className="mt-2 text-[10px] text-muted-foreground text-center">
          {wins}W · {losses}L{draws ? ` · ${draws}D` : ''} this season
        </p>
      )}
      {pendingRewards.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="pending-rewards">
          {pendingRewards.map((r) => (
            <button
              key={r.season_id}
              onClick={() => onClaim(r.season_id)}
              disabled={claimingSeason === r.season_id}
              data-testid={`claim-reward-${r.season_id}`}
              className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-yellow-400 text-black font-semibold px-3 py-2 text-xs flex items-center justify-between shadow-md hover:from-amber-400 hover:to-yellow-300 disabled:opacity-50"
            >
              <span className="flex items-center gap-1.5">
                <Trophy className="w-3.5 h-3.5" />
                Claim {r.season_name} · {r.final_tier}
              </span>
              <span className="font-mono">
                {claimingSeason === r.season_id ? '...' : `+${r.reward?.coins || 0} coins`}
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

